import { Provider } from 'react-redux'
import { BrowserRouter } from 'react-router-dom'
import { PersistGate } from 'redux-persist/integration/react'
import store, { persistor } from './store'
import * as Sentry from '@sentry/react'
import Theme from '@/components/template/Theme'
import Notification from '@/components/ui/Notification'
import toast from '@/components/ui/toast'
import Layout from '@/components/layouts'
import mockServer from './mock'
import history from './history'
import appConfig from '@/configs/app.config'
import './locales'
import { v4 as uuidv4 } from 'uuid'
import { isEmpty } from 'lodash'
import useClarity from '@/utils/hooks/useClarity';
import IntercomHandler from '@/utils/intercom/IntercomHandler';
import { CopilotKit } from '@copilotkit/react-core';
import {
    load as loadIntercom,
    boot as bootIntercom,
    update as updateIntercom,
} from './intercom';
import { OpenPanel } from '@openpanel/web';
import MetaPixelTracker from '@/components/shared/MetaPixelTracker';
import MetaPixelInitializer from '@/components/shared/MetaPixelInitializer';
import PostHogInitializer from '@/components/shared/PostHogInitializer';


// Add vite:preloadError event listener for chunk loading failure
window.addEventListener('vite:preloadError', (event) => {
    console.log('Preload error detected. Reloading the page...');
    window.location.reload();  // Force a page reload when a chunk fails to load
  });

const environment = import.meta.env.VITE_NODE_ENV

if (environment !== "production" && appConfig.enableMock) {
    mockServer({ environment })
} else if (environment === "production" || environment === "staging") {
    Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        replaysSessionSampleRate:
            parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE ?? '0.1') ?? 0.1,
        replaysOnErrorSampleRate: 1.0,
        integrations: [
            Sentry.replayIntegration(),
        ],
        environment: environment,
    });
}

history.listen((location) => {
    // Calls Intercom('update') on every page change
    updateIntercom();
});

function App() {
    const clarityId = import.meta.env.VITE_CLARITY_ID;
    const clientId = store.getState().auth?.user?.client_id;
    const email = store.getState().auth.user.email;

    useClarity(clarityId, clientId, email);

    loadIntercom();
    const user_id = store.getState().auth.user.id;
    const userName = store.getState().auth.user.userName;
    const firstName = store.getState().auth.user.first_name;
    const lastName = store.getState().auth.user.last_name;
    Sentry.setUser({ email: email });
    const threadId = uuidv4();

    interface LocalStorageData {
        organizationName: string | null;
        plan: string | null;
        orgCreatedAt: string | null;
        monthlyPayment: string | null;
        lastPaymentDate: string | null;
        lastPaymentAmount: string | null;
        orgStatus: string | null;
    }

    let localStorageData: LocalStorageData = {
        organizationName: null,
        plan: null,
        orgCreatedAt: null,
        monthlyPayment: null,
        lastPaymentDate: null,
        lastPaymentAmount: null,
        orgStatus: null,
    };

    try {
        localStorageData = {
            organizationName: localStorage.getItem("ORGANIZATION_NAME"),
            plan: localStorage.getItem("PLAN"),
            orgCreatedAt: localStorage.getItem("ORG_CREATED_AT"),
            monthlyPayment: localStorage.getItem("MONTHLY_PAYMENT"),
            lastPaymentDate: localStorage.getItem("LAST_PAYMENT_DATE"),
            lastPaymentAmount: localStorage.getItem("LAST_PAYMENT_AMOUNT"),
            orgStatus: localStorage.getItem("ORG_STATUS"),
        };
    } catch (error) {
        alert("Enable cookies in your browser settings for HelpYouSponsor to work.");
    }

    const op = new OpenPanel({
        clientId: 'f7580e39-8850-4800-8a90-326f03a73baa',
        trackScreenViews: true,
        trackOutgoingLinks: true,
        trackAttributes: true,
    });

    op.identify({
        profileId: user_id,
        firstName: firstName,
        lastName: lastName,
        email: email,
        properties: {
            id: store.getState().auth.user.client_id,
            name: localStorageData.organizationName,
            plan: localStorageData.plan,
            created_at: localStorageData.orgCreatedAt,
            monthly_spend: localStorageData.monthlyPayment,
            last_payment_date: localStorageData.lastPaymentDate,
            last_payment_amount: `$${localStorageData.lastPaymentAmount}`,
            organization_status: localStorageData.orgStatus,
        },
    });

    op.track('app_open', { foo: 'bar' });

    // Meta Pixel initialization is now handled by MetaPixelInitializer component
    // which uses data from the API instead of environment variables

    if (isEmpty(email) && isEmpty(user_id)) {
        bootIntercom();
    } else {
        bootIntercom({
            email: email,
            user_id: user_id,
            userName: userName,
            client_id: store.getState().auth.user.client_id,
            organization: localStorage.getItem("ORGANIZATION_NAME"),
            created_at: store.getState().auth.user.created_at,
            signed_up: store.getState().auth.user.created_at,
            company: {
                id: store.getState().auth.user.client_id,
                name: localStorageData.organizationName,
                plan: localStorageData.plan,
                created_at: localStorageData.orgCreatedAt,
                monthly_spend: localStorageData.monthlyPayment,
                last_payment_date: localStorageData.lastPaymentDate,
                last_payment_amount: `$${localStorageData.lastPaymentAmount}`,
                organization_status: localStorageData.orgStatus,
            },
        })
    }
    return (
        <Sentry.ErrorBoundary showDialog>
            <CopilotKit
                runtimeUrl={import.meta.env.VITE_COPILOT_URL}
                properties={{
                    email: email,
                    thread_id: threadId,
                    client_id: store.getState().auth.user.client_id,
                    user_id: user_id
                }}
            >
                <Provider store={store}>
                    <PersistGate loading={null} persistor={persistor}>
                        <BrowserRouter>
                            <Theme>
                            <IntercomHandler 
                                userData={store.getState().auth.user} 
                                localStorageData={localStorageData} 
                            />
                            <PostHogInitializer />
                            <MetaPixelInitializer 
                                userData={store.getState().auth.user}
                                localStorageData={localStorageData}
                            />
                            <MetaPixelTracker />
                                <Layout />
                            </Theme>
                        </BrowserRouter>
                    </PersistGate>
                </Provider>
            </CopilotKit>
        </Sentry.ErrorBoundary>
    )
}

export default App
